import { KIYOH, kiyohHasWidget, kiyohWidgetSrc } from "@/lib/reviews";
import { kiyohScoreLabel, kiyohStars, useKiyohStats } from "@/lib/kiyoh-stats";
import { useT } from "@/lib/i18n";
import { ReviewStars } from "@/components/ReviewStars";

/**
 * Kiyoh-waardering, in twee maten die bewust ánders werken.
 *
 * `size="md"` — het échte Kiyoh-widget (iframe). Alleen op plekken met ruimte: de kop van
 * /reviews, het reviewblok op de homepage en de info-tabbladen. Toont het live cijfer, dus dat kan
 * nooit uit de pas lopen met Kiyoh zelf.
 *
 * `size="sm"` — een eigen, compacte weergave. GEEN iframe, en dat is een bewuste keuze: deze badge
 * staat onder élke productkaart, dus op een categoriepagina zouden dat twintig iframes naar
 * kiyoh.com worden. Dat kost meer laadtijd dan de badge waard is.
 *
 * Het cijfer in de kleine badge komt uit [[src/lib/kiyoh-stats.ts]] — één gecachte Firestore-read,
 * gevoed door de nachtelijke `syncKiyohStats`-functie (de Kiyoh-API eist een sleutel, en die hoort
 * niet in de browser). Zolang er nog geen gepubliceerde beoordelingen zijn, blijft dat leeg en
 * tonen we grijze sterren zónder cijfer: liever geen waardering dan een verzonnen waardering.
 */

/**
 * Vijf sterren, gedeeltelijk gevuld tot `value` (op 5). Grijs als er nog geen cijfer is.
 * Gebruikt [[src/components/ReviewStars.tsx]] zodat winkel- en productbeoordelingen er identiek
 * uitzien; twee eigen sterrentekeningen liepen anders uit elkaar zodra er één werd bijgesteld.
 */
function Stars({ value }: { value: number | null }) {
  return <ReviewStars value={value ?? 0} size={16} />;
}

function CompactBadge() {
  const t = useT();
  const stats = useKiyohStats();
  const inhoud = (
    <>
      {/*
        Sterren + alleen het aantal ("4 reviews"), zoals bezoekers het van andere webshops kennen.
        Het cijfer zelf stond hier eerst voluit ("Winkel 10,0 / 10") — dat leest als een regel
        techniek in plaats van als een waardering. Voor wie het wél wil weten: de badge linkt naar
        ons Kiyoh-profiel, en het voorleeslabel hieronder noemt het volledige cijfer én dat het om
        de winkel gaat, niet om dit ene artikel.
      */}
      <Stars value={stats ? kiyohStars(stats.average) : null} />
      {/*
        Zolang het cijfer nog niet binnen is: grijze sterren, GEEN tekst. Er stond hier eerst
        "Beoordelingen via Kiyoh" als terugval, en omdat het cijfer één tel later uit Firestore
        komt, zag je bij elke paginawissel die tekst kort opflitsen en dan omslaan naar "4 reviews".
        Dat leest als een storing — en de naam van het platform hoort sowieso niet als vulling
        onder een productkaart. Lege ruimte van dezelfde hoogte wisselt niet en valt niet op.
      */}
      <span className="ml-1 min-h-4 text-sm font-medium text-primary-700">
        {stats ? `${stats.count} ${stats.count === 1 ? t("review") : t("reviews")}` : ""}
      </span>
    </>
  );
  const label = stats
    ? `${t("Winkelbeoordeling via Kiyoh")}: ${kiyohScoreLabel(stats.average)}/10 (${stats.count})`
    : t("Nog geen beoordelingen");
  if (!KIYOH.profileUrl) {
    return <span className="inline-flex items-center gap-0.5" aria-label={label}>{inhoud}</span>;
  }
  return (
    <a
      href={KIYOH.profileUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      // stopPropagation: deze badge staat binnen een klikbare productkaart; zonder dit opent een
      // klik op de sterren óók de productpagina eronder.
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-0.5 hover:opacity-80"
    >
      {inhoud}
    </a>
  );
}

export function KiyohBadge({
  size = "sm",
  thema = "licht",
  breedte = 400,
  hoogte = 222,
}: {
  size?: "sm" | "md";
  thema?: "licht" | "donker";
  /** Afmeting van het insluitkader. Kiyoh herschikt de inhoud zelf: 400×222 is de brede variant
   *  (ring + tekst + knop naast elkaar), 120×160 de smalle staande. Alleen bij `size="md"`. */
  breedte?: number;
  hoogte?: number;
}) {
  const t = useT();
  if (size === "sm" || !kiyohHasWidget()) return <CompactBadge />;
  return (
    <iframe
      src={kiyohWidgetSrc(thema)}
      title={t("Beoordelingen van Glastotaal op Kiyoh")}
      width={breedte}
      height={hoogte}
      loading="lazy"
      // Achtergrond doorzichtig laten: het widget tekent er zelf geen, dus het neemt de kleur van
      // het vlak eronder over. Een rand eromheen botst met onze kaartstijl.
      style={{ border: 0, maxWidth: "100%", background: "transparent" }}
    />
  );
}
